/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { API_BASE_URL, API_PREFIX, STORAGE_KEYS } from '../../config/api'

/**
 * Types pour l'API
 */
export interface User {
  id: number
  email: string
  email_verified: boolean
  name: string
  oauth_provider: string | null
  created_at: string
  updated_at: string
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  is_new_user?: boolean
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RefreshRequest {
  refresh_token: string
}

export interface APIError {
  detail: string | Record<string, unknown>
  status?: number
}

/**
 * Classe d'erreur HTTP personnalisée qui préserve le code de statut
 */
export class HTTPError extends Error {
  constructor(
    message: string,
    public status: number,
    public error?: APIError
  ) {
    super(message)
    this.name = 'HTTPError'
  }
}

/**
 * Client API pour communiquer avec le backend FastAPI
 */
class APIClient {
  private baseURL: string
  private accessToken: string | null = null
  private refreshToken: string | null = null
  private refreshPromise: Promise<TokenResponse> | null = null

  constructor() {
    this.baseURL = API_BASE_URL + API_PREFIX

    // Charger les tokens depuis le localStorage au démarrage
    this.accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    this.refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  }

  /**
   * Définir les tokens d'authentification
   */
  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken
    this.refreshToken = refreshToken

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
  }

  /**
   * Effacer les tokens (logout)
   */
  clearTokens(): void {
    this.accessToken = null
    this.refreshToken = null

    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER)
  }

  /**
   * Obtenir le token d'accès courant
   * Lit toujours depuis localStorage pour être synchronisé avec les modifications externes
   */
  getAccessToken(): string | null {
    // Synchroniser avec localStorage au cas où les tokens auraient été modifiés
    // par un autre onglet ou directement dans le storage
    const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (storedToken !== this.accessToken) {
      this.accessToken = storedToken
      this.refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    }
    return this.accessToken
  }

  /**
   * Rafraîchir le token d'accès
   */
  async refreshAccessToken(): Promise<TokenResponse> {
    // Si un refresh est déjà en cours, retourner la même promise
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    if (!this.refreshToken) {
      throw new Error('No refresh token available')
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseURL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        })

        if (!response.ok) {
          throw new Error('Failed to refresh token')
        }

        const data: TokenResponse = await response.json()
        this.setTokens(data.access_token, data.refresh_token)

        return data
      } finally {
        this.refreshPromise = null
      }
    })()

    return this.refreshPromise
  }

  /**
   * Effectuer une requête HTTP avec gestion automatique du JWT
   */
  private async request<T>(endpoint: string, options: RequestInit = {}, retry = true): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    // Ajouter le token d'accès si disponible
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // Si 401 et qu'on a un refresh token, essayer de rafraîchir
      if (response.status === 401 && this.refreshToken && retry) {
        await this.refreshAccessToken()
        // Réessayer la requête avec le nouveau token
        return this.request<T>(endpoint, options, false)
      }

      if (!response.ok) {
        const error: APIError = await response.json().catch(() => ({
          detail: `HTTP Error ${response.status}`,
          status: response.status,
        }))
        const detailMsg =
          typeof error.detail === 'string'
            ? error.detail
            : (((error.detail as Record<string, unknown>)?.message as string | undefined) ??
              `HTTP Error ${response.status}`)
        throw new HTTPError(detailMsg, response.status, error)
      }

      // 204 No Content ou corps vide : ne pas tenter de parser du JSON
      const contentLength = response.headers.get('content-length')
      const contentType = response.headers.get('content-type') ?? ''

      if (response.status === 204 || contentLength === '0') {
        return null as T
      }

      // Toujours tenter de parser le body en JSON pour les 2xx
      // Même si Content-Type est absent ou inattendu (réponse CGI/proxy altérée)
      // Cela évite un retour null silencieux qui cause "Cannot read properties of null"
      if (contentType.includes('application/json') || contentType === '') {
        return response.json()
      }

      // Content-Type présent et non-JSON : tenter quand même le parse (corps peut être JSON)
      // puis lever une erreur explicite si ça échoue
      try {
        return await response.json()
      } catch {
        const bodyText = await response.text().catch(() => '(body unreadable)')
        throw new Error(
          `Réponse inattendue du serveur (Content-Type: ${contentType || 'absent'}). ` +
            `Extrait: ${bodyText.slice(0, 200)}`
        )
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Network error')
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  // ========== Endpoints d'authentification ==========

  /**
   * Inscription d'un nouvel utilisateur
   * Retourne un message de confirmation (l'utilisateur doit vérifier son email avant de se connecter)
   */
  async register(data: RegisterRequest): Promise<{ message: string; email: string }> {
    return this.post<{ message: string; email: string }>('/auth/register', data)
  }

  /**
   * Connexion d'un utilisateur
   */
  async login(data: LoginRequest): Promise<TokenResponse> {
    const response = await this.post<TokenResponse>('/auth/login', data)
    this.setTokens(response.access_token, response.refresh_token)
    return response
  }

  /**
   * Récupérer les informations de l'utilisateur connecté
   */
  async getCurrentUser(): Promise<User> {
    return this.get<User>('/auth/me')
  }

  /**
   * Déconnexion
   */
  logout(): void {
    this.clearTokens()
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  isAuthenticated(): boolean {
    return this.accessToken !== null
  }

  /**
   * Vérifier l'email avec le token
   * Retourne les tokens JWT pour connecter automatiquement l'utilisateur
   */
  async verifyEmailCode(
    email: string,
    code: string
  ): Promise<TokenResponse & { message: string; email: string }> {
    const response = await this.post<TokenResponse & { message: string; email: string }>(
      '/auth/verify-email',
      { email, code }
    )
    this.setTokens(response.access_token, response.refresh_token)
    return response
  }

  /**
   * Renvoyer l'email de vérification
   */
  async resendVerification(email: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/auth/resend-verification', { email })
  }

  /**
   * Connexion via Google OAuth
   */
  async loginWithGoogle(idToken: string): Promise<TokenResponse> {
    const response = await this.post<TokenResponse>('/auth/google', { id_token: idToken })
    this.setTokens(response.access_token, response.refresh_token)
    return response
  }

  /**
   * Demande de réinitialisation de mot de passe
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/auth/forgot-password', { email })
  }

  /**
   * Réinitialisation du mot de passe via code à 6 chiffres
   */
  async resetPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> {
    return this.post<{ message: string }>('/auth/reset-password', {
      email,
      code,
      new_password: newPassword,
    })
  }

  /**
   * Changement de mot de passe (utilisateur connecté)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  }

  /**
   * Connexion via Apple OAuth
   */
  async loginWithApple(
    idToken: string,
    userData?: Record<string, unknown>
  ): Promise<TokenResponse> {
    const response = await this.post<TokenResponse>('/auth/apple', {
      id_token: idToken,
      user_data: userData,
    })
    this.setTokens(response.access_token, response.refresh_token)
    return response
  }
}

/**
 * Instance singleton du client API
 */
export const apiClient = new APIClient()
